import {
  useGetEnrollmentAccountOwnershipData,
  useSubmitEnrollmentAccountOwnershipData,
  useSubmitEnrollmentJointSelectAccountData,
} from '@egw/api'
import { ActionTile } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { useTranslation } from '@egw/i18n'
import { EnrollmentV2Layout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { riskService } from '@egw/risk-service'
import { useRouter } from '@egw/router'
import { CheckIcon } from '@heroicons/react/20/solid'
import { UserIcon, UsersIcon } from '@heroicons/react/24/solid'
import { type FC } from 'react'
import { PageHeader } from '../../../components'
import { ROUTES as GENERIC_ROUTES } from '../../../constants/routes/routes-generic'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'

const log = new Logger('Enrollment - Account Ownership')

const DisabledChip: FC = () => {
  const { t } = useTranslation('features-enrollment')
  return (
    <div className="flex flex-row items-center gap-3xs rounded-full bg-gray-100 px-2xs py-3xs w-fit">
      <CheckIcon className="w-3 h-3 fill-icons-full" />
      <p className="typo-primary-2xs-semibold text-text-meta">{t('investments_v2.account_selection.already_opened')}</p>
    </div>
  )
}

export const AccountOwnershipPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  // API hooks
  const { data } = useGetEnrollmentAccountOwnershipData()
  const { mutate } = useSubmitEnrollmentAccountOwnershipData({})
  const { mutate: submitEnrollmentJointSelectAccountData } = useSubmitEnrollmentJointSelectAccountData()

  const canEnrollJointBrokerage = data?.canEnrollJointBrokerage

  // Start individual enrollment
  const onSubmit = async (): Promise<void> => {
    try {
      // Reset enrollment session before initiating application
      await enrollmentCookieService.resetSession()
      await riskService.updateFlow('enrollment')
      const response = await mutate({
        initiateApplication: true,
      })

      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        navigate(response.path)
      } else if (!response?.path) {
        // If no path from BE, redirect to error
        navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  // Start joint enrollment
  const onSubmitJoint = async (): Promise<void> => {
    try {
      // Reset enrollment session before initiating application
      await enrollmentCookieService.resetSession()
      await riskService.updateFlow('enrollment')
      const response = await submitEnrollmentJointSelectAccountData({
        initiateApplication: true,
      })

      // If path exists and is different
      if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
        navigate(response.activePerson.path)
      } else if (!response?.activePerson?.path) {
        // If no path from BE, redirect to error
        navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const closeHandler = (): void => {
    navigate(WEALTH_ROUTES.INVESTMENTS.path)
  }

  return (
    <EnrollmentV2Layout closeHandler={closeHandler}>
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {/* TODO: Keeping loading until BE is defined */}
        {/* {isLoading ? <EnrollmentPageLoading testId="select_account-loading" /> : null} */}

        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          <PageHeader title={t('investments.account-ownership.title')} />
          <div className="w-full flex-1 flex flex-col justify-start">
            <span className="typo-primary-base-semibold text-text-header mb-md mt-md">
              {t('investments.account-ownership.description')}
            </span>
            <div className="flex flex-col gap-md">
              <ActionTile
                title={t('investments.account-ownership.individual_title')}
                description=""
                icon={<UserIcon className="w-6 h-6 mr-sm" />}
                onClick={() => void onSubmit()}
                testId="individual-account-tile"
              />
              <ActionTile
                title={t('investments.account-ownership.joint_title')}
                description=""
                icon={<UsersIcon className="w-6 h-6 mr-sm" />}
                onClick={() => void onSubmitJoint()}
                testId="joint-account-tile"
                disabled={!canEnrollJointBrokerage}
              >
                {!canEnrollJointBrokerage ? <DisabledChip /> : null}
              </ActionTile>
            </div>

            <div className="flex-1 md:hidden" />
          </div>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {/* {submitError ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null} */}
      </div>
    </EnrollmentV2Layout>
  )
}
